import { HttpEvent, HttpEventType, HttpProgressEvent, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { distinctUntilChanged, scan } from 'rxjs/operators';

const isHttpResponse = <T>(event: HttpEvent<T>): event is HttpResponse<T> => {
  return event.type === HttpEventType.Response;
};

const isHttpProgressEvent = (event: HttpEvent<unknown>): event is HttpProgressEvent => {
  return event.type === HttpEventType.DownloadProgress || event.type === HttpEventType.UploadProgress;
};

export interface Download {
  content: ArrayBuffer | null;
  progress: number;
  state: 'PENDING' | 'IN_PROGRESS' | 'DONE';
  total?: number;
  loaded?: number;
}

export const downloadWithProgress = (
  saver?: (b: ArrayBuffer) => void,
): ((source: Observable<HttpEvent<ArrayBuffer>>) => Observable<Download>) => {
  let total: number = undefined;
  return (source: Observable<HttpEvent<ArrayBuffer>>) =>
    source.pipe(
      scan(
        (download: Download, event): Download => {
          if (isHttpProgressEvent(event)) {
            if (total === undefined) {
              total = parseFloat((event.total / 1024 / 1024).toFixed(2));
            }

            const progressToReturn = {
              total: parseFloat((event.total / 1024 / 1024).toFixed(2)),
              loaded: parseFloat((event.loaded / 1024 / 1024).toFixed(2)),
              progress: event.total ? Math.round((100 * event.loaded) / event.total) : download.progress,
              state: 'IN_PROGRESS',
              content: null,
            } as Download;

            return progressToReturn;
          }

          if (isHttpResponse(event)) {
            if (saver) {
              saver(event.body);
            }

            return { progress: 100, state: 'DONE', content: event.body, total, loaded: total };
          }
          return download;
        },
        { state: 'PENDING', progress: 0, content: null },
      ),
      distinctUntilChanged((a, b) => a.state === b.state && a.progress === b.progress && a.content === b.content),
    );
};
